import * as React from "react"
import { Head, router } from "@inertiajs/react"
import { ArrowLeft, Loader2, Send, Sparkles, Square } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { DeliveryTicks } from "@/components/ui/delivery-ticks"
import { DateSeparator } from "@/components/ui/date-separator"
import { cn } from "@/lib/utils"
import { ConversationSidebar, type ConversationSummary } from "./ConversationSidebar"
import { TrendChart, type TrendPoint } from "./TrendChart"
import { V2Chart, type V2Point } from "./V2Chart"

const PATH = "/admin/assistant"

// Layout effects don't run during SSR (React warns if you use them there), but scroll
// preservation must run before paint on the client to avoid a flicker — pick the right one.
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect
const DESCRIPTION =
  "Tanya dalam Bahasa Indonesia tentang penjualan bisnis — total, tren, perbandingan, ranking, rincian, dan alasan pergerakan — dijawab dari data (Primary/Secondary Sales, Sell-Out, Service Level, Stok, Market Share), bukan karangan."

const EXAMPLES = [
  "Berapa total primary sales Februari 2026?",
  "Tren secondary sales 12 bulan terakhir",
  "Top 5 produk primary sales Februari 2026",
  "Bandingkan primary sales Januari 2026 vs Februari 2026",
  "Service level Indomaret Februari 2026",
  "Kenapa sales Vitalis Glam Slay naik di Desember 2025?",
]

type ProductRef = { id: number; canonical_name: string; brand?: string; pack_size?: string }
type PeriodRef = { year: number; month: number; label: string }
type DecompRow = { key: string; label: string; value: number | null; kind: string }
type DriverRow = {
  driver_type: string
  label: string
  direction: string | null
  count: number
  magnitude: number
  summary?: string | null
}
type Narration = { status?: string; narrated?: boolean }

// ── #53 agentic turn metadata (`payload.agent` — a namespace, not flat keys) ─────
type ToolTraceStep = { tool: string; input?: string; summary?: string; ms?: number; error?: boolean }
type AgentMeta = {
  status?: "thinking" | "answered" | "fallback" | "failed"
  grounding?: string
  reason?: string
  model?: string
  steps?: number
  ms?: number
  tool_trace?: ToolTraceStep[]
}

// ── v2 flexible-answer shapes ───────────────────────────────────────────────────
type V2Metric = { id: string; label: string; unit: string; echelon: string }
type V2TableColumn = { label: string; align?: string }
type V2TableRow = { cells: (string | number)[] }
type V2Table = { title?: string; columns: V2TableColumn[]; rows: V2TableRow[] }
type V2ChartData = { title?: string; unit?: string; series: V2Point[] }
type V2Headline = {
  value?: number | null
  value_b?: number | null
  unit?: string
  delta?: number | null
  delta_pct?: number | null
  direction?: string | null
}

type Payload = {
  kind?: string
  status?: string
  phrase?: string
  reason?: string
  intent?: string
  metric?: V2Metric
  table?: V2Table
  chart?: { series: TrendPoint[] }
  v2_chart?: V2ChartData
  grounding?: { sot_validated?: boolean; source?: string; grain?: string }
  deterministic_text?: string
  resolution?: {
    product?: ProductRef
    period?: PeriodRef
    period_b?: PeriodRef
    account?: { label: string }
    comparison?: string
    comparison_period?: PeriodRef
  }
  // Loose headline: v1 ("why") fields + v2 (total/compare) fields, all optional.
  headline?: {
    net_value?: number | null
    prev_value?: number | null
    delta_value?: number | null
    delta_pct?: number | null
    mom_pct?: number | null
    yoy_pct?: number | null
    direction?: string | null
    counterparty_count?: number | null
  } & V2Headline
  decomposition?: DecompRow[]
  drivers?: DriverRow[]
  candidates?: ProductRef[]
  parsed?: { period: PeriodRef | null; comparison: string }
  narration?: Narration
  agent?: AgentMeta
}

// A turn is still being worked on: the agentic loop is running, or the LLM is
// rephrasing a deterministic answer. Either way the page keeps polling.
function isPending(payload: Payload): boolean {
  if (payload.agent?.status === "thinking") return true
  return payload.narration?.status === "pending" || payload.narration?.status === "narrating"
}

type MessageStatus = "sending" | "sent" | "streaming" | "done" | "error" | "stopped"

type Message = {
  id: number
  conversation_id?: number
  role: "user" | "assistant"
  content: string
  payload: Payload
  status?: MessageStatus
  client_token?: string | null
  error_reason?: string | null
  created_at: string
  // Client-only: an optimistic bubble not yet confirmed by the server (#54 M3).
  optimistic?: boolean
  // Client-only (#54 M6): a dropped stream is being re-fetched (same token), and a
  // give-up state after the reconnect budget is spent (offers an explicit retry).
  reconnecting?: boolean
  loadError?: boolean
}

type Props = {
  conversations: ConversationSummary[]
  active_conversation_id: number | null
  messages: Message[]
  // Older turns remain beyond the newest-N window the server rendered (#54 M6) — lights
  // the "Muat pesan lama" control.
  messages_has_more: boolean
  assistant_enabled: boolean
  // Reference "today" in Asia/Jakarta (YYYY-MM-DD) from the server — drives the
  // "Hari ini" / "Kemarin" date separators deterministically (#54 M5).
  server_today: string
}

// ── formatting ────────────────────────────────────────────────────────────────
function fmtIdr(value: number | null | undefined): string {
  if (typeof value !== "number" || Number.isNaN(value)) return "—"
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(value)
}
function fmtIdrSigned(value: number | null | undefined): string {
  if (typeof value !== "number" || Number.isNaN(value)) return "—"
  return `${value < 0 ? "−" : "+"}${fmtIdr(Math.abs(value))}`
}
function fmtSignedPct(value: number | null | undefined, digits = 1): string {
  if (typeof value !== "number" || Number.isNaN(value)) return "—"
  return `${value > 0 ? "+" : ""}${value.toFixed(digits)}%`
}
function fmtCell(row: DecompRow): string {
  switch (row.kind) {
    case "idr":
      return fmtIdr(row.value)
    case "idr_signed":
      return fmtIdrSigned(row.value)
    case "pct_signed":
      return fmtSignedPct(row.value)
    default:
      return row.value == null ? "—" : String(row.value)
  }
}

// ── #54 M5: SSR-deterministic day bucketing, labels & timestamps ──────────────────
// Everything here is computed from the server-provided `created_at` + a server `today`
// reference — never the client clock — so date separators and per-message times render
// identically during SSR and after hydration (no `Date.now()`, no locale drift).
const JAKARTA_TZ = "Asia/Jakarta"

// The Asia/Jakarta calendar day (YYYY-MM-DD) an ISO timestamp falls in. Empty for an
// optimistic bubble that has no server time yet — callers fall back to `today`.
function jakartaDayKey(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: JAKARTA_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d)
}

// HH:MM in Asia/Jakarta. Empty for an optimistic (not-yet-persisted) bubble.
function jakartaTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return new Intl.DateTimeFormat("id-ID", {
    timeZone: JAKARTA_TZ,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d)
}

// Shift a YYYY-MM-DD day key by whole days — pure UTC arithmetic, no clock.
function shiftDayKey(key: string, deltaDays: number): string {
  const [y, m, d] = key.split("-").map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + deltaDays)
  return dt.toISOString().slice(0, 10)
}

// "9 Juli 2026" from a YYYY-MM-DD key (noon UTC avoids any off-by-one).
function formatFullDate(key: string): string {
  const [y, m, d] = key.split("-").map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d, 12))
  return new Intl.DateTimeFormat("id-ID", { timeZone: "UTC", day: "numeric", month: "long", year: "numeric" }).format(dt)
}

// "Hari ini" / "Kemarin" / an explicit date, relative to the SERVER reference day.
function dayLabel(dayKey: string, todayKey: string): string {
  const key = dayKey || todayKey
  if (key === todayKey) return "Hari ini"
  if (key === shiftDayKey(todayKey, -1)) return "Kemarin"
  return formatFullDate(key)
}

// A flat thread becomes day separators + messages tagged with grouping flags: a run of
// same-sender messages on the same day shows its avatar/label only on the first and its
// timestamp only on the last (messenger convention). Pure — safe to run during SSR.
type RenderItem =
  | { kind: "separator"; key: string; label: string }
  | {
      kind: "message"
      key: string | number
      message: Message
      firstOfGroup: boolean
      lastOfGroup: boolean
      timeLabel: string
    }

function buildRenderList(messages: Message[], todayKey: string): RenderItem[] {
  const out: RenderItem[] = []
  let prevDay: string | null = null
  messages.forEach((m, i) => {
    const day = jakartaDayKey(m.created_at) || todayKey
    if (day !== prevDay) {
      out.push({ kind: "separator", key: `sep-${day}-${i}`, label: dayLabel(day, todayKey) })
      prevDay = day
    }
    const prev = messages[i - 1]
    const next = messages[i + 1]
    const prevDayKey = prev ? jakartaDayKey(prev.created_at) || todayKey : null
    const nextDayKey = next ? jakartaDayKey(next.created_at) || todayKey : null
    out.push({
      kind: "message",
      key: m.client_token ?? m.id,
      message: m,
      firstOfGroup: !prev || prev.role !== m.role || prevDayKey !== day,
      lastOfGroup: !next || next.role !== m.role || nextDayKey !== day,
      timeLabel: jakartaTime(m.created_at),
    })
  })
  return out
}

// Two vocabularies reach this badge: the v1 driver engine says "up"/"down", the v2
// Composer (and so every agentic answer) says "naik"/"turun". Both must light it.
function DirectionBadge({ direction }: { direction: string | null }) {
  if (direction === "up" || direction === "naik") return <Badge tone="success">▲ naik</Badge>
  if (direction === "down" || direction === "turun") return <Badge tone="danger">▼ turun</Badge>
  return <Badge tone="muted">•</Badge>
}

function Prose({ text }: { text: string }) {
  const paras = (text ?? "").trim().split(/\n{2,}/).filter(Boolean)
  if (paras.length === 0) return <p className="text-ink-muted">—</p>
  return (
    <div className="space-y-2">
      {paras.map((p, i) => (
        <p key={i} className="whitespace-pre-line">
          {p}
        </p>
      ))}
    </div>
  )
}

// ── #54 M7: text-equivalent labels for the (recharts SVG) answer charts ───────────
// A `role="img"` wrapper turns the chart into a single labelled node for a screen reader —
// the raw SVG paths become presentational, and the reader hears a one-line summary of the
// series instead of an unlabelled canvas. The <table> renderers stay the full text
// equivalent for the exact numbers.
function trendChartSummary(series: TrendPoint[]): string {
  if (series.length === 0) return "Grafik tren sell-in (tidak ada data)"
  const first = series[0]
  const last = series[series.length - 1]
  const peak = series.reduce((a, b) => (b.net_value > a.net_value ? b : a), first)
  return `Grafik tren sell-in ${series.length} bulan: dari ${first.label} ${fmtIdr(first.net_value)} ke ${last.label} ${fmtIdr(last.net_value)}; puncak ${peak.label} ${fmtIdr(peak.net_value)}.`
}

function v2ChartSummary(chart: V2ChartData, metricLabel?: string): string {
  const pts = chart.series.filter((p): p is V2Point & { net_value: number } => typeof p.net_value === "number")
  const subject = chart.title || metricLabel || "nilai"
  if (pts.length === 0) return `Grafik ${subject} (tidak ada data)`
  const first = pts[0]
  const last = pts[pts.length - 1]
  return `Grafik ${subject}: dari ${first.label} ${fmtV2(first.net_value, chart.unit)} ke ${last.label} ${fmtV2(last.net_value, chart.unit)} sepanjang ${pts.length} titik.`
}

// ── assistant answer body (text already rendered by the bubble) ─────────────────
function AnswerBody({ payload }: { payload: Payload }) {
  const { headline, resolution } = payload
  return (
    <div className="mt-3 space-y-4">
      {headline && resolution && (
        <div className="flex flex-wrap items-center gap-2">
          <DirectionBadge direction={headline.direction ?? null} />
          <Badge tone="accent">{fmtSignedPct(headline.delta_pct)}</Badge>
          <span className="text-sm text-ink-muted">
            {resolution.comparison === "yoy" ? "YoY" : "MoM"} · {fmtIdrSigned(headline.delta_value)} ·{" "}
            {headline.counterparty_count ?? 0} counterparty
          </span>
        </div>
      )}

      {payload.chart && payload.chart.series.length > 0 && (
        <div>
          <h3 className="mb-1 text-sm font-medium text-ink-display">Tren 12 bulan — nilai sell-in (PS)</h3>
          <div role="img" aria-label={trendChartSummary(payload.chart.series)}>
            <TrendChart data={payload.chart.series} />
          </div>
        </div>
      )}

      {payload.decomposition && payload.decomposition.length > 0 && (
        <div>
          <h3 className="mb-1 text-sm font-medium text-ink-display">Dekomposisi</h3>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[20rem] text-sm">
              <tbody>
                {payload.decomposition.map((row) => (
                  <tr key={row.key} className="border-b border-hairline last:border-0">
                    <td className="py-1.5 pr-4 text-ink-muted">{row.label}</td>
                    <td className="py-1.5 text-right tabular-nums text-ink-body">{fmtCell(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {payload.drivers && payload.drivers.length > 0 && (
        <div>
          <h3 className="mb-1 text-sm font-medium text-ink-display">
            Pendorong ter-ranking <span className="font-normal text-ink-muted">(ko-okurensi, bukan sebab)</span>
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[24rem] text-sm">
              <thead>
                <tr className="border-b border-hairline text-left text-xs text-ink-muted">
                  <th className="py-1.5 pr-4 font-medium">Pendorong</th>
                  <th className="py-1.5 pr-4 font-medium">Arah</th>
                  <th className="py-1.5 pr-4 font-medium text-right">Jumlah</th>
                  <th className="py-1.5 font-medium text-right">Magnitudo</th>
                </tr>
              </thead>
              <tbody>
                {payload.drivers.map((d, i) => (
                  <tr key={`${d.driver_type}-${d.direction}-${i}`} className="border-b border-hairline last:border-0">
                    <td className="py-1.5 pr-4 text-ink-body">{d.label}</td>
                    <td className="py-1.5 pr-4">
                      <DirectionBadge direction={d.direction} />
                    </td>
                    <td className="py-1.5 pr-4 text-right tabular-nums text-ink-body">{d.count}</td>
                    <td className="py-1.5 text-right tabular-nums text-ink-body">{d.magnitude.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

// ── v2 flexible answer (total · trend · compare · rank · breakdown) ──────────────
function fmtV2(value: number | null | undefined, unit?: string): string {
  if (typeof value !== "number" || Number.isNaN(value)) return "—"
  if (unit === "pct") return `${new Intl.NumberFormat("id-ID", { maximumFractionDigits: 2 }).format(value)}%`
  if (unit === "qty") return `${new Intl.NumberFormat("id-ID").format(Math.round(value))} unit`
  return fmtIdr(value)
}

function V2TableView({ table }: { table: V2Table }) {
  return (
    <div>
      {table.title && <h3 className="mb-1 text-sm font-medium text-ink-display">{table.title}</h3>}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[20rem] text-sm">
          <thead>
            <tr className="border-b border-hairline text-left text-xs text-ink-muted">
              {table.columns.map((c, i) => (
                <th key={i} className={cn("py-1.5 pr-4 font-medium", c.align === "right" && "text-right")}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((r, ri) => (
              <tr key={ri} className="border-b border-hairline last:border-0">
                {r.cells.map((cell, ci) => (
                  <td
                    key={ci}
                    className={cn(
                      "py-1.5 pr-4 tabular-nums text-ink-body",
                      table.columns[ci]?.align === "right" && "text-right",
                    )}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function V2Answer({ payload }: { payload: Payload }) {
  const { metric, headline, v2_chart, table, resolution } = payload
  const unit = metric?.unit
  const isCompare = payload.intent === "compare"
  return (
    <div className="mt-3 space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {metric && <Badge tone="accent">{metric.label}</Badge>}
        {resolution?.period && <Badge tone="muted">{resolution.period.label}</Badge>}
        {resolution?.product && <Badge tone="muted">{resolution.product.canonical_name}</Badge>}
        {resolution?.account && <Badge tone="muted">{resolution.account.label}</Badge>}
        {isCompare && headline?.direction && <DirectionBadge direction={headline.direction ?? null} />}
        {isCompare && typeof headline?.delta_pct === "number" && (
          <Badge tone="accent">{fmtSignedPct(headline.delta_pct)}</Badge>
        )}
        {!isCompare && typeof headline?.value === "number" && (
          <span className="text-sm font-medium text-ink-display tabular-nums">{fmtV2(headline.value, unit)}</span>
        )}
      </div>

      {v2_chart && v2_chart.series.length > 0 && (
        <div>
          {v2_chart.title && <h3 className="mb-1 text-sm font-medium text-ink-display">{v2_chart.title}</h3>}
          <div role="img" aria-label={v2ChartSummary(v2_chart, metric?.label)}>
            <V2Chart data={v2_chart.series} unit={v2_chart.unit} label={metric?.label} />
          </div>
        </div>
      )}

      {table && table.rows.length > 0 && <V2TableView table={table} />}

      {payload.grounding?.sot_validated && (
        <p className="text-xs text-ink-muted">Angka tervalidasi dari SOT (fact tables) — bukan karangan.</p>
      )}
    </div>
  )
}

function Candidates({ payload }: { payload: Payload }) {
  const ask = (c: ProductRef) => {
    let q = c.canonical_name
    const parsed = payload.parsed
    if (parsed?.period) q += ` ${parsed.period.label}`
    if (parsed?.comparison === "yoy") q += " vs tahun lalu"
    router.post(PATH, { question: q }, { preserveScroll: true })
  }
  return (
    <div className="mt-3 flex flex-col gap-2">
      {(payload.candidates ?? []).map((c) => (
        <Button
          key={c.id}
          variant="secondary"
          size="sm"
          className="h-auto justify-start whitespace-normal py-2 text-left"
          onClick={() => ask(c)}
        >
          {c.canonical_name}
        </Button>
      ))}
    </div>
  )
}

// ── #53 the agentic loop is running: spinner + copy, no prose, no badges ────────
function ThinkingBody() {
  return (
    <div className="flex items-center gap-2 text-ink-muted" role="status" aria-live="polite">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      <span className="text-sm">Mata Langit sedang menghitung…</span>
    </div>
  )
}

// ── #54 M4: the SSE stream has opened but no prose has arrived yet ─────────────────
function TypingBody() {
  return (
    <div className="flex items-center gap-2 text-ink-muted" role="status" aria-live="polite">
      <span className="animate-pulse text-sm">Mata Langit sedang mengetik…</span>
    </div>
  )
}

// ── #54 M6: a dropped stream is being re-fetched (same token — no re-run, no dup) ─────
function ReconnectingBody() {
  return (
    <div className="flex items-center gap-2 text-ink-muted" role="status" aria-live="polite">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      <span className="text-sm">Menyambung ulang…</span>
    </div>
  )
}

// The reconnect budget is spent: offer an explicit retry (still the SAME token — replays
// the persisted answer if it exists, otherwise re-asks; never duplicates the turn).
function ReconnectFailedBody({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-ink-muted">
      <span>Gagal memuat jawaban.</span>
      <button
        type="button"
        onClick={onRetry}
        className="rounded font-medium text-ink-body underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        Coba lagi
      </button>
    </div>
  )
}

// The tool calls that produced THIS answer. Rendered only on `answered` turns: a
// `fallback` turn's prose comes from the deterministic pipeline, so its (rejected)
// trace would not describe the numbers above it. Collapsed by default.
function ToolTrace({ trace }: { trace: ToolTraceStep[] }) {
  return (
    <details className="mt-3 border-t border-hairline pt-2">
      <summary className="cursor-pointer rounded text-sm text-ink-muted hover:text-ink-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
        Cara saya menghitung <span className="tabular-nums">({trace.length} langkah)</span>
      </summary>
      <ol className="mt-2 space-y-3">
        {trace.map((step, i) => (
          <li key={i}>
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={step.error ? "danger" : "muted"}>{step.tool}</Badge>
              {typeof step.ms === "number" && (
                <span className="text-xs tabular-nums text-ink-muted">{step.ms} ms</span>
              )}
            </div>
            {step.input && <TraceCode text={step.input} muted />}
            {step.summary && <TraceCode text={step.summary} />}
          </li>
        ))}
      </ol>
    </details>
  )
}

// Clipped JSON from the trace — scrolls sideways rather than widening the bubble.
function TraceCode({ text, muted = false }: { text: string; muted?: boolean }) {
  return (
    <pre
      className={cn(
        "mt-1 overflow-x-auto rounded border border-hairline bg-page px-2 py-1 text-xs",
        muted ? "text-ink-muted" : "text-ink-body",
      )}
    >
      <code>{text}</code>
    </pre>
  )
}

function AssistantBubble({
  message,
  firstOfGroup,
  lastOfGroup,
  timeLabel,
  onReconnect,
}: {
  message: Message
  firstOfGroup: boolean
  lastOfGroup: boolean
  timeLabel: string
  onReconnect: () => void
}) {
  const { payload } = message
  const agent = payload.agent
  const thinking = agent?.status === "thinking"
  const reconnecting = Boolean(message.reconnecting)
  const loadError = Boolean(message.loadError)
  const streaming = message.status === "streaming" && !thinking
  const stopped = message.status === "stopped"
  const errored = message.status === "error"
  const narrating =
    !thinking && !streaming && (payload.narration?.status === "pending" || payload.narration?.status === "narrating")
  const trace = agent?.status === "answered" ? (agent.tool_trace ?? []) : []
  // The grounded cards ride the final `done` event only — never mid-stream, on a stop, or
  // while a dropped stream is reconnecting.
  const showCards = !streaming && !stopped && !reconnecting && !loadError
  // Grouping (#54 M5): the "Mata Langit" avatar/label shows only on the first of a run;
  // per-message state badges still show on any turn that carries them.
  const showHeader = firstOfGroup || narrating || Boolean(payload.narration?.narrated) || stopped
  // Timestamp on the last of a run — but not while the turn is still typing/thinking/
  // reconnecting (an optimistic streaming bubble has no persisted time yet).
  const showTime = lastOfGroup && Boolean(timeLabel) && !streaming && !thinking && !reconnecting && !loadError

  return (
    <div className={cn(firstOfGroup ? "mt-4" : "mt-1")}>
      <div className="flex justify-start">
        <div className="max-w-full rounded-lg rounded-tl-sm border border-hairline bg-surface px-4 py-3 sm:max-w-[85%]">
          {/* Grouped (non-first) assistant turns hide the visible "Mata Langit" label — keep the
              sender available to a screen reader so every bubble still conveys who is speaking. */}
          {!firstOfGroup && <span className="sr-only">Mata Langit: </span>}
          {showHeader && (
            <div className="mb-1 flex items-center gap-2">
              {firstOfGroup && (
                <>
                  <Sparkles className="h-4 w-4 text-accent" aria-hidden="true" />
                  <span className="text-xs font-medium text-ink-muted">Mata Langit</span>
                </>
              )}
              {narrating && <Badge tone="accent">menarasikan…</Badge>}
              {payload.narration?.narrated && <Badge tone="muted">AI</Badge>}
              {stopped && <Badge tone="muted">Dihentikan</Badge>}
            </div>
          )}

          {thinking ? (
            <ThinkingBody />
          ) : loadError ? (
            <ReconnectFailedBody onRetry={onReconnect} />
          ) : reconnecting ? (
            <ReconnectingBody />
          ) : streaming && !message.content ? (
            <TypingBody />
          ) : (
            <>
              <Prose text={message.content} />
              {errored && !message.content && (
                <p className="text-sm text-ink-muted">Gagal menjawab — coba kirim lagi.</p>
              )}
              {showCards && payload.kind === "answer" && <AnswerBody payload={payload} />}
              {showCards && payload.kind === "v2_answer" && <V2Answer payload={payload} />}
              {showCards && payload.kind === "ambiguous" && <Candidates payload={payload} />}
              {showCards && trace.length > 0 && <ToolTrace trace={trace} />}
            </>
          )}
        </div>
      </div>
      {showTime && (
        <div className="mt-0.5 flex justify-start">
          <time dateTime={message.created_at} className="text-[0.7rem] tabular-nums text-ink-muted">
            <span className="sr-only">Dijawab pukul </span>
            {timeLabel}
          </time>
        </div>
      )}
    </div>
  )
}

function UserBubble({
  message,
  firstOfGroup,
  lastOfGroup,
  timeLabel,
  onRetry,
}: {
  message: Message
  firstOfGroup: boolean
  lastOfGroup: boolean
  timeLabel: string
  onRetry: () => void
}) {
  const status = message.status
  return (
    <div className={cn("flex flex-col items-end", firstOfGroup ? "mt-4" : "mt-1")}>
      <div className="max-w-full whitespace-pre-line rounded-lg rounded-tr-sm bg-accent/10 px-4 py-2 text-ink-body sm:max-w-[85%]">
        <span className="sr-only">Anda: </span>
        {message.content}
      </div>
      <div className="mt-0.5 flex items-center gap-1.5 text-[0.7rem] text-ink-muted">
        {status === "error" ? (
          <span className="flex items-center gap-1.5 text-danger-display">
            Gagal terkirim
            <button
              type="button"
              onClick={onRetry}
              className="rounded font-medium underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              Coba lagi
            </button>
          </span>
        ) : (
          <>
            {lastOfGroup && timeLabel && (
              <time dateTime={message.created_at} className="tabular-nums">
                <span className="sr-only">Dikirim pukul </span>
                {timeLabel}
              </time>
            )}
            {status === "sending" && <DeliveryTicks state="sending" />}
            {status === "sent" && <DeliveryTicks state="sent" />}
          </>
        )}
      </div>
    </div>
  )
}

// ── #54 M3/M4: non-form send — SSE fetch-stream, optimistic ───────────────────────
const MESSAGES_PATH = (conversationId: number) => `${PATH}/conversations/${conversationId}/messages`

// The SSE frames the send endpoint streams (Accept: text/event-stream).
type StreamEvent =
  | { type: "user"; user_message: Message; conversation: ConversationSummary }
  | { type: "start"; assistant_message_id: number; client_token: string }
  | { type: "delta"; text: string }
  | { type: "done"; assistant_message: Message }

function csrfToken(): string {
  if (typeof document === "undefined") return ""
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
}

// Open the SSE stream on the SAME endpoint (`Accept: text/event-stream`) via `fetch`, and
// dispatch each frame. `signal` lets the Stop button abort mid-stream. `client_token` keeps
// it idempotent server-side.
async function streamMessage(
  conversationId: number,
  clientToken: string,
  content: string,
  signal: AbortSignal,
  onEvent: (evt: StreamEvent) => void,
): Promise<void> {
  const res = await fetch(MESSAGES_PATH(conversationId), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      "X-CSRF-Token": csrfToken(),
      "X-Requested-With": "XMLHttpRequest",
    },
    credentials: "same-origin",
    body: JSON.stringify({ client_token: clientToken, content }),
    signal,
  })
  if (!res.ok || !res.body) throw new Error(`stream failed: ${res.status}`)

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let sep = buffer.indexOf("\n\n")
    while (sep >= 0) {
      const frame = buffer.slice(0, sep)
      buffer = buffer.slice(sep + 2)
      const dataLine = frame.split("\n").find((l) => l.startsWith("data:"))
      if (dataLine) onEvent(JSON.parse(dataLine.slice(5).trim()) as StreamEvent)
      sep = buffer.indexOf("\n\n")
    }
  }
}

function newClientToken(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID()
  return `ct-${Math.floor(performance.now())}-${Math.floor(Math.random() * 1e9)}`
}

// The optimistic assistant bubble's local token, derived from the user token so a retry
// can clear the whole failed pair (user + assistant placeholder) before re-sending.
const asstPlaceholder = (token: string) => `asst-for-${token}`
const userTokenOf = (asstToken: string) => asstToken.replace(/^asst-for-/, "")

// #54 M6 — raw-JSON fetch endpoints (never the Inertia router).
const OLDER_MESSAGES_PATH = (conversationId: number, before: number) =>
  `${MESSAGES_PATH(conversationId)}?before=${before}`
const STOP_PATH = (conversationId: number) => `${MESSAGES_PATH(conversationId)}/stop`

// Reconnect budget: after this many failed re-fetches of a dropped stream we give up and
// offer an explicit retry. Backoff is bounded exponential (0.8s, 1.6s, 3.2s).
const MAX_RECONNECTS = 3
const reconnectBackoff = (attempt: number) => Math.min(4000, 400 * 2 ** attempt)

// A delay that a Stop (abort) can cut short — so backing off between reconnect attempts
// never blocks the Stop button.
function abortableDelay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(new DOMException("aborted", "AbortError"))
    const t = setTimeout(resolve, ms)
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(t)
        reject(new DOMException("aborted", "AbortError"))
      },
      { once: true },
    )
  })
}

// #54 M6 — multi-tab: what one tab broadcasts to its peers on the same conversation.
type BroadcastMsg =
  | { type: "turn"; conversation_id: number; user_message: Message | null; assistant_message: Message }
  | { type: "conversations" }

// Fold peer-broadcast turns into the thread WITHOUT duplication: skip anything already
// present by client_token or id, and drop any local optimistic bubble the incoming real
// row supersedes. Real rows sort by id; optimistic bubbles stay last. This is the
// multi-tab idempotency guard — a replayed turn can never double.
function mergeIncoming(prev: Message[], incoming: (Message | null | undefined)[]): Message[] {
  const seenTokens = new Set(prev.map((m) => m.client_token).filter(Boolean))
  const seenIds = new Set(prev.map((m) => m.id))
  const additions = incoming.filter(
    (m): m is Message => !!m && !(m.client_token != null && seenTokens.has(m.client_token)) && !seenIds.has(m.id),
  )
  if (additions.length === 0) return prev
  const addedTokens = new Set(additions.map((m) => m.client_token).filter(Boolean))
  const kept = prev.filter((m) => !(m.optimistic && m.client_token != null && addedTokens.has(m.client_token)))
  const key = (m: Message) => (m.optimistic ? Number.MAX_SAFE_INTEGER : m.id)
  return [...kept, ...additions].sort((a, b) => key(a) - key(b))
}

// Auto-growing composer: Enter sends, Shift+Enter inserts a newline, empty is disabled,
// focus returns after a send. While a stream is in flight the send button becomes a Stop
// button (aborts generation). Controlled by the page so example prompts can prefill it.
function Composer({
  value,
  onChange,
  onSend,
  onStop,
  sending,
  inputRef,
}: {
  value: string
  onChange: (v: string) => void
  onSend: () => void
  onStop: () => void
  sending: boolean
  inputRef: React.RefObject<HTMLTextAreaElement | null>
}) {
  React.useEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`
  }, [value, inputRef])

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      if (!sending) onSend()
    }
  }

  const empty = !value.trim()

  return (
    // Docked composer: sticks above the sticky day separators (z-20 > z-10) and pads for the
    // mobile home-indicator safe area so the keyboard/gesture bar never overlaps the field.
    <div className="sticky bottom-0 z-20 mt-6 flex items-end gap-2 bg-page pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <Textarea
        ref={inputRef}
        id="assistant-question"
        name="question"
        rows={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Tulis pertanyaan… (Enter kirim · Shift+Enter baris baru)"
        aria-label="Pertanyaan"
        className="max-h-[200px] min-h-[2.5rem] resize-none"
      />
      {sending ? (
        <Button type="button" variant="secondary" onClick={onStop} aria-label="Stop generating">
          <Square className="h-4 w-4" />
          <span className="ml-1 hidden sm:inline">Stop</span>
        </Button>
      ) : (
        <Button type="button" onClick={onSend} disabled={empty} aria-label="Kirim">
          <Send className="h-4 w-4" />
          <span className="ml-1 hidden sm:inline">Kirim</span>
        </Button>
      )}
    </div>
  )
}

export default function Index({
  conversations,
  active_conversation_id,
  messages,
  messages_has_more,
  assistant_enabled,
  server_today,
}: Props) {
  const endRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLTextAreaElement>(null)
  const tempId = React.useRef(-1)
  const streamRef = React.useRef<AbortController | null>(null)
  const activeToken = React.useRef<string | null>(null)
  const bcRef = React.useRef<BroadcastChannel | null>(null)
  // #54 M7 — a11y refs: the message-log region (focus lands here after loading older turns
  // if the trigger disappears) and the "Muat pesan lama" button (focus returns to it while
  // more pages remain), so a keyboard/screen-reader user is never dropped to <body>.
  const logRef = React.useRef<HTMLDivElement>(null)
  const loadMoreRef = React.useRef<HTMLButtonElement>(null)
  // Scroll preservation on prepend (#54 M6): remember the pre-prepend scroll height, and
  // skip the newest-message auto-scroll for that one render.
  const prependHeight = React.useRef<number | null>(null)
  const skipAutoScroll = React.useRef(false)
  const [draft, setDraft] = React.useState("")
  const [sending, setSending] = React.useState(false)
  const [hasMore, setHasMore] = React.useState(messages_has_more)
  const [loadingOlder, setLoadingOlder] = React.useState(false)
  // Server props are the source of truth; `localMessages` layers optimistic bubbles on
  // top and folds in the fetched answer without a page reload. `localConversations` lets
  // the send update the sidebar (auto-title, reorder) with no Inertia reload at all.
  const [localMessages, setLocalMessages] = React.useState<Message[]>(messages)
  const [localConversations, setLocalConversations] = React.useState<ConversationSummary[]>(conversations)
  const [mobilePane, setMobilePane] = React.useState<"list" | "thread">(active_conversation_id ? "thread" : "list")
  // #54 M7 — a screen-reader announcement of the SETTLED assistant answer (the full prose,
  // once), driven by an effect below rather than per-token so the reader isn't spammed. The
  // visible thread is a `role="log"` with `aria-live="off"`; this dedicated polite region is
  // the single controlled channel for "the answer has landed".
  const [liveAnswer, setLiveAnswer] = React.useState("")
  // Seed with the last already-rendered assistant turn so a page load / navigation does NOT
  // read old history aloud — only genuinely new settled answers get announced.
  const lastAnnounced = React.useRef<number | null>(
    (() => {
      for (let i = messages.length - 1; i >= 0; i--) if (messages[i].role === "assistant") return messages[i].id
      return null
    })(),
  )

  // Sidebar follows the server whenever a real Inertia visit changes it (navigate, rename,
  // archive, new chat). The optimistic send updates it locally instead (below).
  React.useEffect(() => {
    setLocalConversations(conversations)
  }, [conversations])

  React.useEffect(() => {
    setMobilePane(active_conversation_id ? "thread" : "list")
  }, [active_conversation_id])

  React.useEffect(() => {
    setHasMore(messages_has_more)
  }, [messages_has_more, active_conversation_id])

  // Reconcile when the server view changes (navigation, or a poll reload): reset to the
  // server window, but KEEP (a) already-loaded OLDER pages for this conversation and (b)
  // in-flight optimistic bubbles the server hasn't caught up to. Both are scoped to the
  // active conversation so a switch never bleeds one thread's bubbles into another (#54 M6).
  React.useEffect(() => {
    setLocalMessages((prev) => {
      const serverTokens = new Set(messages.map((m) => m.client_token).filter(Boolean))
      const serverIds = new Set(messages.map((m) => m.id))
      const oldestServerId = messages.length ? messages[0].id : Number.POSITIVE_INFINITY
      const olderLoaded = prev.filter(
        (m) =>
          !m.optimistic && !serverIds.has(m.id) && m.id < oldestServerId && m.conversation_id === active_conversation_id,
      )
      const stillPending = prev.filter(
        (m) => m.optimistic && m.client_token && !serverTokens.has(m.client_token) && m.conversation_id === active_conversation_id,
      )
      return [...olderLoaded, ...messages, ...stillPending]
    })
  }, [messages, active_conversation_id])

  // Auto-scroll to the newest message — but never on a prepend (loading older turns keeps
  // your place, handled by the layout effect below).
  React.useEffect(() => {
    if (skipAutoScroll.current) {
      skipAutoScroll.current = false
      return
    }
    endRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [localMessages.length, active_conversation_id])

  // #54 M7 — announce the newest assistant turn to a screen reader ONCE it has settled:
  // never while streaming/thinking/reconnecting (that would read a half-typed answer), and
  // never for a turn already announced. Covers every transport (streamed deterministic,
  // polled agentic, replayed on reconnect) because it watches the reconciled thread, not the
  // send path. The full prose is announced — the honest "settled answer, once" the user chose.
  React.useEffect(() => {
    let settled: Message | undefined
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const m = localMessages[i]
      if (m.role !== "assistant") continue
      if (m.optimistic || m.reconnecting || m.status === "streaming" || m.status === "sending") return
      if (isPending(m.payload)) return
      settled = m
      break
    }
    if (!settled || settled.id === lastAnnounced.current) return
    lastAnnounced.current = settled.id
    if (settled.status === "stopped") setLiveAnswer("Jawaban dihentikan.")
    else if (settled.status === "error") setLiveAnswer("Gagal menjawab. Silakan kirim lagi.")
    else setLiveAnswer(settled.content?.trim() || "Jawaban selesai.")
  }, [localMessages])

  // #54 M7 — sensible focus landing when a conversation is opened or switched: put the cursor
  // in the composer (messenger convention) so a keyboard/screen-reader user starts ready to
  // type instead of on <body>. Streaming doesn't steal focus (send keeps it here already).
  React.useEffect(() => {
    if (active_conversation_id) inputRef.current?.focus()
  }, [active_conversation_id])

  // Preserve scroll position when older messages are prepended: after the DOM grows, nudge
  // scrollTop by exactly the added height so the viewport doesn't jump.
  useIsomorphicLayoutEffect(() => {
    if (prependHeight.current == null) return
    const scroller = document.scrollingElement || document.documentElement
    scroller.scrollTop += scroller.scrollHeight - prependHeight.current
    prependHeight.current = null
  }, [localMessages])

  // Switching conversation mid-stream must abort/detach the in-flight stream cleanly — no
  // cross-thread bleed, no orphaned fetch writing into the wrong thread (#54 M6).
  React.useEffect(() => {
    return () => {
      streamRef.current?.abort()
      streamRef.current = null
    }
  }, [active_conversation_id])

  // Poll only while a turn is unfinished on the SERVER side — the #53 agentic loop is
  // thinking, or the LLM is rephrasing. An SSE stream owns its own turn (its `streaming`
  // bubble is driven by delta events, not polling), so it is deliberately excluded here.
  const pending = localMessages.some((m) => m.role === "assistant" && isPending(m.payload))
  React.useEffect(() => {
    if (!pending) return
    const id = setInterval(() => router.reload({ only: ["messages", "conversations"] }), 2500)
    return () => clearInterval(id)
  }, [pending])

  // Multi-tab reconciliation (#54 M6): peers converge via BroadcastChannel. A turn sent (or
  // a conversation renamed/archived/created) in one tab reflects in the others — messages
  // merged deduped by client_token (never doubled), the sidebar refreshed. Browser-only
  // (opened in an effect, never at module top-level) → SSR-safe. Falls back to a focus poll
  // where BroadcastChannel is unavailable.
  React.useEffect(() => {
    if (typeof window === "undefined") return
    if (typeof BroadcastChannel === "undefined") {
      const onFocus = () => router.reload({ only: ["messages", "conversations"] })
      window.addEventListener("focus", onFocus)
      return () => window.removeEventListener("focus", onFocus)
    }
    const bc = new BroadcastChannel("assistant")
    bcRef.current = bc
    bc.onmessage = (e: MessageEvent<BroadcastMsg>) => {
      const msg = e.data
      if (msg.type === "turn") {
        router.reload({ only: ["conversations"] })
        if (msg.conversation_id === active_conversation_id) {
          setLocalMessages((prev) => mergeIncoming(prev, [msg.user_message, msg.assistant_message]))
        }
      } else if (msg.type === "conversations") {
        router.reload({ only: ["conversations"] })
      }
    }
    return () => {
      bc.close()
      bcRef.current = null
    }
  }, [active_conversation_id])

  const broadcastConversations = () => bcRef.current?.postMessage({ type: "conversations" } satisfies BroadcastMsg)

  const bumpConversation = (c: ConversationSummary) => {
    setLocalConversations((prev) => {
      const others = prev.filter((x) => x.id !== c.id)
      return [c, ...others].sort((a, b) =>
        a.last_message_at < b.last_message_at ? 1 : a.last_message_at > b.last_message_at ? -1 : 0,
      )
    })
  }

  // The core send/stream, shared by a fresh send, a retry, and a reconnect. `token` is the
  // idempotency key: every path re-runs with the SAME token, so the server replays the one
  // persisted turn — never duplicates. On a dropped stream we re-fetch (bounded backoff);
  // an intentional Stop is signaled to the server before we abort so a drop and a Stop
  // finalize differently.
  const runSend = async (text: string, token: string, opts: { reconnectOnly?: boolean } = {}) => {
    if (!active_conversation_id) return
    const convoId = active_conversation_id
    const asstToken = asstPlaceholder(token)
    activeToken.current = token

    if (opts.reconnectOnly) {
      // Manual "Coba lagi" after the reconnect budget was spent: clear the failure and
      // reopen the stream with the same token.
      setLocalMessages((prev) =>
        prev.map((m) =>
          m.optimistic && m.client_token === asstToken ? { ...m, loadError: false, reconnecting: true, status: "streaming" } : m,
        ),
      )
    } else {
      const userTempId = tempId.current--
      const asstTempId = tempId.current--
      // Optimistic: drop any prior (errored) attempt for this token, echo the user bubble
      // instantly, and open an empty assistant bubble in a `streaming` (typing) state. Both
      // are stamped with the conversation id so a thread switch can scope them out.
      setLocalMessages((prev) => [
        ...prev.filter((m) => !(m.optimistic && (m.client_token === token || m.client_token === asstToken))),
        { id: userTempId, conversation_id: convoId, role: "user", content: text, payload: {}, status: "sending", client_token: token, created_at: "", optimistic: true },
        { id: asstTempId, conversation_id: convoId, role: "assistant", content: "", payload: {}, status: "streaming", client_token: asstToken, created_at: "", optimistic: true },
      ])
    }
    setSending(true)
    requestAnimationFrame(() => inputRef.current?.focus())

    const controller = new AbortController()
    streamRef.current = controller
    const findAsst = (m: Message) => m.optimistic === true && m.client_token === asstToken
    let lastUser: Message | null = null
    let stopped = false

    for (let attempt = 0; ; ) {
      let sawDone = false
      try {
        await streamMessage(convoId, token, text, controller.signal, (evt) => {
          if (streamRef.current !== controller) return // stale/superseded stream — ignore late frames
          if (evt.type === "user") {
            lastUser = { ...evt.user_message, optimistic: false }
            setLocalMessages((prev) =>
              prev.map((m) => (m.optimistic && m.client_token === token ? { ...evt.user_message, optimistic: false } : m)),
            )
            bumpConversation(evt.conversation)
          } else if (evt.type === "delta") {
            setLocalMessages((prev) => prev.map((m) => (findAsst(m) ? { ...m, content: m.content + evt.text, reconnecting: false } : m)))
          } else if (evt.type === "done") {
            sawDone = true
            const done = evt.assistant_message
            setLocalMessages((prev) => prev.map((m) => (findAsst(m) ? { ...done, optimistic: false } : m)))
            bcRef.current?.postMessage({ type: "turn", conversation_id: convoId, user_message: lastUser, assistant_message: done } satisfies BroadcastMsg)
          }
        })
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") stopped = true
        // any other error → treat as a broken stream and fall through to reconnect
      }

      if (stopped) {
        // Stop (we signaled the server first): the user turn was persisted → `sent`; the
        // assistant turn keeps what was typed → `stopped`.
        setLocalMessages((prev) =>
          prev.map((m) => {
            if (m.optimistic && m.client_token === token) return { ...m, status: "sent" }
            if (findAsst(m)) return { ...m, status: "stopped", reconnecting: false }
            return m
          }),
        )
        break
      }
      if (sawDone) break

      // Broken stream — re-fetch with the SAME token (server replays; no re-run, no dup).
      attempt += 1
      if (attempt > MAX_RECONNECTS || streamRef.current !== controller) {
        setLocalMessages((prev) => prev.map((m) => (findAsst(m) ? { ...m, reconnecting: false, loadError: true } : m)))
        break
      }
      setLocalMessages((prev) => prev.map((m) => (findAsst(m) ? { ...m, reconnecting: true } : m)))
      try {
        await abortableDelay(reconnectBackoff(attempt), controller.signal)
      } catch {
        stopped = true
        setLocalMessages((prev) =>
          prev.map((m) => {
            if (m.optimistic && m.client_token === token) return { ...m, status: "sent" }
            if (findAsst(m)) return { ...m, status: "stopped", reconnecting: false }
            return m
          }),
        )
        break
      }
    }

    setSending(false)
    streamRef.current = null
    activeToken.current = null
    // Land focus back on the composer after the turn resolves (send / stop / error) — the
    // Stop button unmounts on abort, which would otherwise drop focus to <body>.
    inputRef.current?.focus()
  }

  const send = () => {
    const text = draft.trim()
    if (!text || sending || !active_conversation_id) return
    setDraft("")
    void runSend(text, newClientToken())
  }

  // Re-POST a failed user message with its ORIGINAL client_token — idempotent server-side.
  const retry = (message: Message) => {
    if (sending || !message.client_token) return
    void runSend(message.content, message.client_token)
  }

  // Explicit retry of a dropped answer whose reconnect budget was spent: reopen the stream
  // with the same token (the user turn already exists — the server replays / re-answers).
  const reconnect = (message: Message) => {
    if (sending) return
    const token = userTokenOf(message.client_token ?? "")
    const userMsg = localMessages.find((m) => m.client_token === token && m.role === "user")
    if (!userMsg) return
    void runSend(userMsg.content, token, { reconnectOnly: true })
  }

  // Stop is a deliberate cancel: tell the server BEFORE aborting so it finalizes the turn
  // `stopped` (no cards) rather than treating the cut socket as a recoverable drop.
  const stop = async () => {
    const controller = streamRef.current
    const token = activeToken.current
    if (!controller || !active_conversation_id) return
    if (token) {
      try {
        await fetch(STOP_PATH(active_conversation_id), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken(),
            "X-Requested-With": "XMLHttpRequest",
          },
          credentials: "same-origin",
          body: JSON.stringify({ client_token: token }),
        })
      } catch {
        // best-effort — even if the signal fails, aborting still stops the UI
      }
    }
    controller.abort()
  }

  // Load the previous page of older turns and prepend them, preserving scroll position.
  const loadOlder = async () => {
    if (loadingOlder || !active_conversation_id) return
    const cursor = localMessages.reduce(
      (min, m) => (!m.optimistic && typeof m.id === "number" && m.id > 0 && m.id < min ? m.id : min),
      Number.POSITIVE_INFINITY,
    )
    if (!Number.isFinite(cursor)) return
    setLoadingOlder(true)
    try {
      const res = await fetch(OLDER_MESSAGES_PATH(active_conversation_id, cursor), {
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin",
      })
      if (!res.ok) return
      const data = (await res.json()) as { messages: Message[]; has_more: boolean }
      if (data.messages.length > 0) {
        const scroller = document.scrollingElement || document.documentElement
        prependHeight.current = scroller.scrollHeight
        skipAutoScroll.current = true
        setLocalMessages((prev) => {
          const existing = new Set(prev.map((m) => m.id))
          const fresh = data.messages.filter((m) => !existing.has(m.id))
          return [...fresh, ...prev]
        })
      }
      setHasMore(data.has_more)
      // Keep focus off <body> after the prepend: back on the trigger while more pages remain,
      // otherwise on the log region itself (the newly loaded turns are at the top of it).
      requestAnimationFrame(() => (data.has_more ? loadMoreRef.current?.focus() : logRef.current?.focus()))
    } finally {
      setLoadingOlder(false)
    }
  }

  const prefill = (ex: string) => {
    setDraft(ex)
    requestAnimationFrame(() => inputRef.current?.focus())
  }

  const activeTitle = localConversations.find((c) => c.id === active_conversation_id)?.title ?? "Tanya Mata Langit"

  // Interleave day separators + tag each message with grouping flags. Recomputed only when
  // the thread or the server reference day changes; deterministic (SSR-safe).
  const renderList = React.useMemo(() => buildRenderList(localMessages, server_today), [localMessages, server_today])

  return (
    <>
      <Head title="Tanya Mata Langit">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Tanya Mata Langit" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>

      <AppShell width="wide">
        <h1 className="sr-only">Tanya Mata Langit</h1>
        <div className="flex gap-6">
          {/* Conversation list (sidebar) */}
          <aside
            className={cn(
              "w-full shrink-0 md:sticky md:top-6 md:h-[calc(100vh-3rem)] md:w-72",
              mobilePane === "list" ? "block" : "hidden md:block",
            )}
          >
            <ConversationSidebar
              conversations={localConversations}
              activeId={active_conversation_id}
              onNavigate={() => setMobilePane("thread")}
              onChanged={broadcastConversations}
            />
          </aside>

          {/* Active conversation thread */}
          <section
            className={cn("min-w-0 flex-1 flex-col", mobilePane === "thread" ? "flex" : "hidden md:flex")}
          >
            <div className="flex items-center gap-2 border-b border-hairline pb-3">
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden"
                onClick={() => setMobilePane("list")}
                aria-label="Kembali ke daftar percakapan"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-0">
                <h2 className="truncate">{activeTitle}</h2>
                {!assistant_enabled && (
                  <p className="text-xs text-ink-muted">Mode deterministik — jawaban dirakit langsung dari data.</p>
                )}
              </div>
            </div>

            {/* Screen-reader announcement channel for the settled answer (see the effect
                above). Outside the log so the log's aria-live="off" can't gate it. */}
            <div className="sr-only" aria-live="polite" aria-atomic="true">
              {liveAnswer}
            </div>

            {/* The message thread is a log region: a navigable landmark, but aria-live is off
                so bubble additions and streaming text mutations don't spam a screen reader —
                announcements are funnelled through the dedicated region above + the per-state
                role="status" indicators. */}
            <div
              ref={logRef}
              role="log"
              aria-label="Riwayat percakapan Tanya Mata Langit"
              aria-live="off"
              tabIndex={-1}
              className="mt-4 flex-1 focus:outline-none"
            >
              {localMessages.length === 0 ? (
                <div className="rounded-lg border border-dashed border-hairline p-6 text-sm text-ink-muted">
                  <p className="mb-3">Mulai dengan pertanyaan seperti:</p>
                  <div className="flex flex-col gap-2">
                    {EXAMPLES.map((ex) => (
                      <Button
                        key={ex}
                        variant="secondary"
                        size="sm"
                        // Wrap long prompts instead of overflowing the card at narrow widths
                        // (WCAG 1.4.10 Reflow — no horizontal scroll on mobile). (#54 M7)
                        className="h-auto justify-start whitespace-normal py-2 text-left"
                        onClick={() => prefill(ex)}
                      >
                        {ex}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {hasMore && (
                    <div className="mb-2 flex justify-center">
                      <Button ref={loadMoreRef} variant="secondary" size="sm" onClick={loadOlder} disabled={loadingOlder}>
                        {loadingOlder ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                            <span className="ml-1">Memuat…</span>
                          </>
                        ) : (
                          "Muat pesan lama"
                        )}
                      </Button>
                    </div>
                  )}
                  {renderList.map((item) =>
                    item.kind === "separator" ? (
                      <DateSeparator key={item.key} label={item.label} />
                    ) : item.message.role === "user" ? (
                      <UserBubble
                        key={item.key}
                        message={item.message}
                        firstOfGroup={item.firstOfGroup}
                        lastOfGroup={item.lastOfGroup}
                        timeLabel={item.timeLabel}
                        onRetry={() => retry(item.message)}
                      />
                    ) : (
                      <AssistantBubble
                        key={item.key}
                        message={item.message}
                        firstOfGroup={item.firstOfGroup}
                        lastOfGroup={item.lastOfGroup}
                        timeLabel={item.timeLabel}
                        onReconnect={() => reconnect(item.message)}
                      />
                    ),
                  )}
                </>
              )}
              <div ref={endRef} />
            </div>

            <Composer value={draft} onChange={setDraft} onSend={send} onStop={stop} sending={sending} inputRef={inputRef} />
          </section>
        </div>
      </AppShell>
    </>
  )
}
